import { useEffect, useState } from "react";
import axios from "axios";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { AutopayAdmin } from "@/components/autopay/AutopayAdmin";
import { Button } from "@/components/ui/button";
import { Mail, Phone, MapPin, Star, FileText, DollarSign, Edit, Download, Send, FileDown, MessageSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePermissions } from "@/hooks/usePermissions";
import { CustomerNotes } from "./CustomerNotes";
import { CustomerDocuments } from "./CustomerDocuments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(n || 0).toFixed(2)}`;
const day = (iso) => String(iso || "").slice(0, 10);

const downloadBlob = async (url, fallback) => {
  const res = await axios.get(url, { responseType: "blob" });
  const match = /filename=([^;]+)/.exec(res.headers["content-disposition"] || "");
  const href = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = href;
  a.download = match ? match[1].trim() : fallback;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(href);
};

const STATUS = {
  paid: "bg-green-100 text-green-700", pending: "bg-orange-100 text-orange-700", sent: "bg-blue-100 text-blue-700",
  partial: "bg-indigo-100 text-indigo-700", approved: "bg-green-100 text-green-700", pending_approval: "bg-orange-100 text-orange-700",
  rejected: "bg-red-100 text-red-700", draft: "bg-slate-100 text-slate-700",
};

const Stat = ({ label, value, cls = "text-slate-800", testId }) => (
  <div className="rounded-lg bg-slate-50 p-3">
    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
    <p className={`text-lg font-bold ${cls}`} data-testid={testId}>{value}</p>
  </div>
);

const Section = ({ title, count, children, testId }) => (
  <section className="space-y-2" data-testid={testId}>
    <h4 className="text-sm font-semibold text-slate-700 flex items-center justify-between">
      {title}
      <Badge variant="outline">{count}</Badge>
    </h4>
    {children}
  </section>
);

export const CustomerQuickView = ({ customerId, onClose, onEdit, canAct = true }) => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirmStatement, setConfirmStatement] = useState(false);
  const [counts, setCounts] = useState({ notes: 0, documents: 0 });
  const [tab, setTab] = useState("overview");

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const msg = await fn();
      toast.success(msg || okMsg);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    } finally {
      setBusy(null);
    }
  };
  const downloadInvoice = (inv) => run(`dl-${inv.id}`, () => downloadBlob(`${API}/invoices/${inv.id}/pdf`, `invoice-${inv.invoice_number}.pdf`), `Downloaded ${inv.invoice_number}`);
  const emailInvoice = (inv) => run(`em-${inv.id}`, async () => (await axios.post(`${API}/invoices/${inv.id}/email`)).data.message, `Emailed ${inv.invoice_number}`);
  const downloadStatement = () => run("stmt-dl", () => downloadBlob(`${API}/customers/${customerId}/statement/pdf`, "statement.pdf"), "Statement downloaded");
  const emailStatement = () => run("stmt-em", async () => (await axios.post(`${API}/customers/${customerId}/statement/email`)).data.message);

  useEffect(() => {
    if (!customerId) return;
    setData(null); setTab("overview"); setCounts({ notes: 0, documents: 0 });
    axios.get(`${API}/customers/${customerId}/overview`)
      .then((r) => setData(r.data))
      .catch(() => { toast.error("Could not load customer details"); onClose(); });
    axios.get(`${API}/customers/${customerId}/records-count`).then((r) => setCounts(r.data)).catch(() => {});
  }, [customerId]);

  const c = data?.customer;
  const recordCount = (counts.notes || 0) + (counts.documents || 0);

  return (
    <Sheet open={!!customerId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="customer-quick-view">
        {!data ? (
          <div className="flex items-center justify-center h-40">
            <SheetTitle className="sr-only">Loading customer</SheetTitle>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
            <SheetHeader>
              <SheetTitle className="text-2xl" data-testid="quick-view-name">{c.name}</SheetTitle>
              <SheetDescription asChild>
                <div className="space-y-1 text-sm text-slate-600">
                  <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-blue-600" /><a href={`mailto:${c.email}`} className="hover:underline">{c.email}</a></p>
                  <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-green-600" />{c.phone}</p>
                  <p className="flex items-start gap-2"><MapPin className="w-4 h-4 text-red-600 mt-0.5" />{c.address}</p>
                </div>
              </SheetDescription>
            </SheetHeader>

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid w-full grid-cols-2" data-testid="quick-view-tabs">
                <TabsTrigger value="overview" data-testid="quick-view-tab-overview">Overview</TabsTrigger>
                <TabsTrigger value="records" className="gap-2" data-testid="quick-view-tab-records">
                  Notes &amp; Documents
                  <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-[11px] justify-center" data-testid="quick-view-records-count">{recordCount}</Badge>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="records" className="space-y-6 mt-4" data-testid="quick-view-records">
                <p className="text-xs text-slate-500 rounded-md bg-slate-50 border border-slate-200 px-3 py-2">Internal only — notes and documents here are never shown to the customer or included in emails or texts.</p>
                <Section title="Notes" count={counts.notes} testId="quick-view-notes">
                  <CustomerNotes customerId={customerId} canEdit={can("customers.edit")} onCountChange={(n) => setCounts((k) => ({ ...k, notes: n }))} />
                </Section>
                <Section title="Documents" count={counts.documents} testId="quick-view-documents">
                  <CustomerDocuments customerId={customerId} canEdit={can("customers.edit")} onCountChange={(n) => setCounts((k) => ({ ...k, documents: n }))} />
                </Section>
              </TabsContent>

              <TabsContent value="overview" className="space-y-6 mt-4" data-testid="quick-view-overview">
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Outstanding" value={money(data.stats.outstanding_total)} cls="text-orange-600" testId="quick-view-outstanding" />
              <Stat label="Paid to date" value={money(data.stats.paid_total)} cls="text-green-600" testId="quick-view-paid" />
              <Stat label="Invoices" value={`${data.stats.open_invoice_count} open / ${data.stats.invoice_count}`} testId="quick-view-invoice-count" />
              <Stat label="Quotes" value={data.stats.quote_count} testId="quick-view-quote-count" />
            </div>

            <Section title="Contact Persons" count={data.contacts.length} testId="quick-view-contacts">
              {data.contacts.length === 0 ? <p className="text-sm text-slate-400">No contact persons</p> : data.contacts.map((ct, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-3 text-sm">
                  <p className="font-medium text-slate-800 flex items-center gap-1">
                    {ct.is_primary && <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />}{ct.name}{ct.title ? <span className="text-slate-500 font-normal"> · {ct.title}</span> : null}
                  </p>
                  {ct.email && <p className="text-slate-600">{ct.email}</p>}
                  {ct.phone && <p className="text-slate-600">{ct.phone}</p>}
                </div>
              ))}
            </Section>

            <Section title="Autopay" count={(data.autopay?.mandates || []).filter((m) => m.status === "active").length} testId="quick-view-autopay">
              <AutopayAdmin customerId={customerId} compact />
            </Section>

            <Section title="Open Invoices" count={data.open_invoices.length} testId="quick-view-open-invoices">
              {data.open_invoices.length === 0 ? <p className="text-sm text-slate-400">No open invoices</p> : data.open_invoices.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3 text-sm" data-testid={`quick-view-invoice-${inv.invoice_number}`}>
                  <div>
                    <p className="font-medium text-slate-800 flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600" />{inv.invoice_number}</p>
                    <p className="text-xs text-slate-500">Due {inv.due_date}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <p className="font-bold text-blue-600">{money(inv.total - (inv.amount_paid || 0))}</p>
                      {inv.amount_paid > 0 && <p className="text-xs text-slate-500">of {money(inv.total)}</p>}
                      <Badge className={`${STATUS[inv.status] || STATUS.draft} text-xs`}>{inv.status.toUpperCase()}</Badge>
                    </div>
                    {canAct && (
                      <div className="flex flex-col gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600 hover:bg-green-50" title="Download PDF" disabled={!!busy} onClick={() => downloadInvoice(inv)} data-testid={`quick-view-download-${inv.invoice_number}`}><Download className="w-4 h-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-orange-600 hover:bg-orange-50" title="Email invoice" disabled={!!busy} onClick={() => emailInvoice(inv)} data-testid={`quick-view-email-${inv.invoice_number}`}><Mail className="w-4 h-4" /></Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {canAct && (
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button size="sm" variant="outline" onClick={downloadStatement} disabled={!!busy} data-testid="quick-view-statement-download">
                    <FileDown className="w-4 h-4 mr-1" /> {busy === "stmt-dl" ? "Preparing…" : "Download Statement"}
                  </Button>
                  <Button size="sm" onClick={() => setConfirmStatement(true)} disabled={!!busy || !c.email} className="bg-blue-600 hover:bg-blue-700" data-testid="quick-view-statement-email">
                    <Send className="w-4 h-4 mr-1" /> {busy === "stmt-em" ? "Sending…" : "Email Statement"}
                  </Button>
                </div>
              )}
            </Section>

            <AlertDialog open={confirmStatement} onOpenChange={setConfirmStatement}>
              <AlertDialogContent data-testid="statement-confirm-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Email statement to {c.name}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    A branded PDF statement listing {data.open_invoices.length} open invoice{data.open_invoices.length === 1 ? "" : "s"} ({money(data.stats.outstanding_total)} outstanding) will be sent to <strong>{c.email}</strong>.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="statement-confirm-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={emailStatement} className="bg-blue-600 hover:bg-blue-700" data-testid="statement-confirm-send">Send Statement</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <Section title="Recent Quotes" count={data.recent_quotes.length} testId="quick-view-quotes">
              {data.recent_quotes.length === 0 ? <p className="text-sm text-slate-400">No quotes yet</p> : data.recent_quotes.map((q) => (
                <div key={q.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3 text-sm" data-testid={`quick-view-quote-${q.quote_number}`}>
                  <div>
                    <p className="font-medium text-slate-800 flex items-center gap-2"><DollarSign className="w-4 h-4 text-indigo-600" />{q.quote_number}</p>
                    <p className="text-xs text-slate-500">{day(q.created_at)} · valid until {q.valid_until}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-indigo-600">{money(q.total)}</p>
                    <Badge className={`${STATUS[q.status] || STATUS.draft} text-xs`}>{q.status.replace("_", " ").toUpperCase()}</Badge>
                  </div>
                </div>
              ))}
            </Section>
              </TabsContent>
            </Tabs>

            <div className="flex gap-2">
              <Button onClick={() => { onEdit(c); onClose(); }} variant="outline" className="flex-1" data-testid="quick-view-edit-button">
                <Edit className="w-4 h-4 mr-2" /> Edit Customer
              </Button>
              <Button onClick={() => { const digits = (c.sms_phone || c.phone || "").replace(/\D/g, ""); const p = digits.length === 10 ? "1" + digits : digits; if (p.length !== 11) return alert("This customer has no valid mobile number"); onClose(); navigate(`/messages?phone=${p}`); }} variant="outline" className="flex-1 text-blue-700 border-blue-300" data-testid="quick-view-text-button">
                <MessageSquare className="w-4 h-4 mr-2" /> Text
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
